import * as React from 'react';

import { Card, CardContent } from 'components/ui/card';
import { CardSlider } from 'components/ui/card-slider';
import { IconBadge } from 'components/ui/icon-badge';
import ResponsiveTooltip from 'components/ui/responsive-tooltip';
import { cn } from 'lib/utils';

/**
 * La tarjeta de una cifra: etiqueta, número y, opcionalmente, nota, icono, tooltip y contenido
 * extra debajo.
 *
 * Historia, porque explica la forma que tiene: nació como `MetricCardV2` en chirigota, se portó
 * aquí, y durante un tiempo convivieron los dos — el del sistema sin que lo usara nadie, y el de
 * la aplicación usado en 19 sitios. Este absorbe lo que le faltaba para que el duplicado
 * desaparezca: `tooltip`, entrada escalonada, `children` y un escape para la cifra.
 */
export interface MetricCardProps {
	/** Opcional — puerto de MyReferralsView.tsx (KPIs de "Invita y gana": Invitados/
	 *  Traspasos/Premios) confirmó un caso real sin icono, solo número+etiqueta. */
	icon?: React.ComponentType<{ className?: string }>;
	label: string;
	value: string;
	sub?: string;
	/** Nota junto al icono, arriba a la derecha. */
	tooltip?: React.ReactNode;
	/**
	 * Posición dentro de un grupo: escalona la entrada 60 ms por posición. Sin él la tarjeta no
	 * anima, que es lo que quieres si va sola.
	 */
	index?: number;
	/**
	 * Escape para la cifra: el tamaño, cuando el valor es texto largo en vez de un importe corto,
	 * o el color cuando una pantalla necesita otro.
	 *
	 * ⚠️ Es un escape a propósito, no una variante. El color por defecto es `--foreground` porque
	 * el verde se reserva para LA cifra destacada de cada pantalla, no para cada número
	 * (Foundations/DarkMode, «Cifras»), y además `--primary` como texto da 3,14:1 en claro, por
	 * debajo de AA. Si pasas un color por aquí, el contraste corre de tu cuenta.
	 */
	valueClassName?: string;
	className?: string;
	children?: React.ReactNode;
}

/**
 * La entrada, en CSS y no con una librería de animación.
 *
 * El original usaba framer-motion. Portarla tal cual habría metido esa dependencia en el sistema
 * de diseño y, como el registry distribuye CÓDIGO FUENTE, se la habría impuesto a todo consumidor
 * que instale esta tarjeta. Con `tw-animate-css`, que Proa ya usa en sus diálogos, sale lo mismo
 * sin añadir nada.
 *
 * La curva es `--ease-brand`, el token de movimiento del sistema, en vez del `easeOut` genérico
 * del original. Va por valor arbitrario porque ese token no está expuesto como utilidad: otro
 * token que existe y no se puede nombrar, del mismo tipo que los que esta épica viene arreglando.
 *
 * `motion-reduce:animate-none` no estaba en el original. Quien pide menos movimiento en su sistema
 * no tiene por qué ver seis tarjetas entrando en cascada.
 */
const ENTRADA =
	'animate-in fade-in-0 slide-in-from-bottom-3 ' +
	'[animation-timing-function:var(--ease-brand)] motion-reduce:animate-none';

export const MetricCard = ({
	icon,
	label,
	value,
	sub,
	tooltip,
	index,
	valueClassName,
	className,
	children
}: MetricCardProps) => (
	<Card
		variant="elevated"
		className={cn(
			'hover:shadow-md transition-shadow h-full',
			index !== undefined && ENTRADA,
			className
		)}
		style={
			index === undefined
				? undefined
				: // `both` para que la tarjeta esté invisible ANTES de que le toque su turno: sin él
					// aparecen las seis de golpe y luego animan, que es peor que no animar.
					//
					// La duración va aquí y no con `duration-[350ms]`: esa utilidad emite DOS reglas y,
					// además de `animation-duration`, fija `transition-duration`. Como `Card` lleva
					// `transition-shadow`, la sombra del hover pasaba a tardar 350 ms solo en las
					// tarjetas con `index` — un efecto colateral que nadie pidió.
					{
						animationDelay: `${index * 60}ms`,
						animationDuration: '350ms',
						animationFillMode: 'both'
					}
		}
	>
		<CardContent className="p-5 sm:p-6">
			{(icon || tooltip) && (
				<div className="mb-2 flex items-start justify-between">
					{icon ? <IconBadge icon={icon} /> : <span />}
					{tooltip && <ResponsiveTooltip content={tooltip} side="top" />}
				</div>
			)}
			<p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
				{label}
			</p>
			{/* 🔴 `text-primary-text`: la cifra va en VERDE, y esto revierte una decisión anterior.
			    Que quede visible, porque no es un detalle de implementación.

			    Hasta aquí iba en `--foreground` por la regla de Foundations/DarkMode, «Cifras»: la
			    maqueta de referencia enseña las tres MetricCard de Mi Beneficio en neutro, y el
			    verde queda reservado para LA cifra destacada de cada pantalla —el saludo, el valor
			    del slider, la proyección—. El argumento era de jerarquía: con todo en el mismo
			    verde no destaca nada.

			    Marisa decide (27/09/26) que la cifra de una MetricCard es de las que deben ir en
			    verde. Se aplica, y con el token correcto: `--primary-text`, no `--primary`. Esa
			    distinción es lo que hace que la reversión NO traiga de vuelta el problema de
			    contraste que la motivó — medido sobre `--card`, que es donde se pinta:

			      --primary        3,14:1   🔴 por debajo del 4,5:1 de AA
			      --foreground    17,77:1
			      --primary-text   7,35:1   ✅ verde y cumpliendo

			    ⚠️ Lo que sigue sin resolver es la jerarquía, no el contraste: en Mi Beneficio el
			    saludo ya dice «tienes 18.420,50 € ahorrados» en verde grande, y la tarjeta de
			    «Total ahorrado» repite ese mismo número. Si Elena mantiene el criterio de la
			    maqueta, lo que hay que cambiar es este valor por defecto, no el token. */}
			<p className={cn('text-2xl font-bold tracking-tight text-primary-text', valueClassName)}>
				{value}
			</p>
			{sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
			{children}
		</CardContent>
	</Card>
);

/**
 * Un grupo de tarjetas de cifra: **un slider en móvil y una rejilla desde `md`**.
 *
 * Tres tarjetas apiladas en 390 px ocupan una pantalla entera y empujan todo lo demás fuera de la
 * vista; en un slider se ven una y un trozo de la siguiente, con puntitos para navegar (mismo
 * criterio que las cards de plan del Resumen, `CardSlider`). Desde `md` caben en fila y vuelven a
 * ser una rejilla, que reparte el ancho entre las que haya.
 *
 * Las dos versiones están siempre en el DOM y las alterna CSS (`md:hidden` / `hidden md:grid`), sin
 * JS de media query. La oculta sale del árbol de accesibilidad, así que no se anuncia dos veces.
 */
export interface MetricCardGroupProps {
	/** Las tarjetas, en orden. Con `index` en cada una, la rejilla entra escalonada. */
	items: MetricCardProps[];
	/** Nombre accesible del slider de móvil. */
	ariaLabel: string;
	className?: string;
}

export const MetricCardGroup = ({ items, ariaLabel, className }: MetricCardGroupProps) => (
	<div className={className}>
		<CardSlider
			className="md:hidden"
			ariaLabel={ariaLabel}
			items={items.map((item) => ({
				id: item.label,
				label: item.label,
				content: <MetricCard {...item} />
			}))}
		/>
		<div className="hidden gap-4 md:grid md:grid-cols-[repeat(auto-fit,minmax(14rem,1fr))]">
			{items.map((item) => (
				<MetricCard key={item.label} {...item} />
			))}
		</div>
	</div>
);
